import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Pencil, Plus, Trash2, X, Check, Save, Loader, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { updateMaterialityAssessmentReport } from './assessmentReportsEditApi.js';
import {
  reportsEditClampScore,
  reportsEditRiskCategory,
  REPORTS_EDIT_SOX_CYCLES,
  REPORTS_EDIT_BS_CATEGORIES,
  REPORTS_EDIT_IS_CATEGORIES,
  reportsEditSoxCycleColor,
  reportsEditValidateCustomSoxCycle,
} from './assessmentReportsEditRiskHelpers.js';

function deepClone(value) {
  return JSON.parse(JSON.stringify(value ?? null));
}

function normalizeRiskScoresShape(soxCycleJson) {
  const sox = deepClone(soxCycleJson) || {};
  let riskScores = sox.riskScores;
  if (Array.isArray(riskScores) && riskScores.length > 0) {
    riskScores = riskScores[0]?.lineItems || riskScores[0] || {};
  }
  if (!riskScores || typeof riskScores !== 'object') {
    riskScores = {};
  }
  if (!riskScores.balance_sheet) riskScores.balance_sheet = { items: [] };
  if (!Array.isArray(riskScores.balance_sheet.items)) riskScores.balance_sheet.items = [];
  if (!riskScores.income_statement) riskScores.income_statement = { items: [] };
  if (!Array.isArray(riskScores.income_statement.items)) riskScores.income_statement.items = [];
  if (!Array.isArray(sox.customSoxCycles)) sox.customSoxCycles = [];

  const normalizeItem = (item) => {
    const label = String(item?.label || item?.line_item || '').trim();
    const score = reportsEditClampScore(item?.risk_score ?? item?.riskScore ?? 5);
    const cycles = Array.isArray(item?.sox_cycles)
      ? item.sox_cycles.map(String)
      : item?.sox_cycle
        ? [String(item.sox_cycle)]
        : [];
    return {
      ...item,
      label,
      line_item: label,
      category: String(item?.category || '').trim().toLowerCase() || 'expense',
      value: Number(item?.value) || 0,
      risk_score: score,
      risk_level: reportsEditRiskCategory(score),
      notes: item?.notes != null ? String(item.notes) : '',
      sox_cycles: cycles,
      sox_cycle: cycles[0] || null,
      units: item?.units || 'USD',
    };
  };

  riskScores.balance_sheet.items = riskScores.balance_sheet.items.map(normalizeItem);
  riskScores.income_statement.items = riskScores.income_statement.items.map(normalizeItem);
  sox.riskScores = riskScores;
  return sox;
}

function categoryOptionsForStatement(statement) {
  return statement === 'balance_sheet' ? REPORTS_EDIT_BS_CATEGORIES : REPORTS_EDIT_IS_CATEGORIES;
}

function formatMoney(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v);
}

function riskBadgeClass(category) {
  if (category === 'HIGH') return 'bg-red-100 text-red-700';
  if (category === 'MODERATE') return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-700';
}

function getSliderBackground() {
  return `linear-gradient(to right,
    rgb(34, 197, 94) 0%,
    rgb(34, 197, 94) 33%,
    rgb(250, 204, 21) 33%,
    rgb(250, 204, 21) 67%,
    rgb(239, 68, 68) 67%,
    rgb(239, 68, 68) 100%)`;
}

/** Matches create-assessment risk modal: live score + green/yellow/red bands. */
function RiskScoreModal({ open, label, score, onClose, onSave }) {
  const [draft, setDraft] = useState(reportsEditClampScore(score));
  useEffect(() => {
    if (open) setDraft(reportsEditClampScore(score));
  }, [open, score]);
  if (!open) return null;

  const original = reportsEditClampScore(score);
  const category = reportsEditRiskCategory(draft);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Editing Risk Score for {label || 'Line Item'}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
          Current:{' '}
          <span className="font-semibold text-gray-900">{original.toFixed(1)}</span>
          {' — '}
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${riskBadgeClass(reportsEditRiskCategory(original))}`}>
            {reportsEditRiskCategory(original)}
          </span>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Adjust Risk Score</label>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">New:</span>
            <span className={`rounded-full px-3 py-1 text-sm font-semibold ${riskBadgeClass(category)}`}>
              {category}
            </span>
            <span className="text-2xl font-bold text-gray-900">{draft.toFixed(1)}</span>
            {draft !== original && (
              <span className="text-sm text-gray-500">(was {original.toFixed(1)})</span>
            )}
          </div>
        </div>

        <input
          type="range"
          min={1}
          max={10}
          step={0.1}
          value={draft}
          onChange={(e) => setDraft(reportsEditClampScore(parseFloat(e.target.value)))}
          className="mb-2 w-full"
          style={{ background: getSliderBackground() }}
        />
        <div className="mb-4 flex justify-between text-xs text-gray-500">
          <span>1.0</span>
          <span>3.3</span>
          <span>6.6</span>
          <span>10.0</span>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-2">
          <div
            className={`rounded py-2 text-center ${
              category === 'LOW' ? 'border-2 border-green-500 bg-green-100' : 'bg-gray-50'
            }`}
          >
            <div className={`text-xs font-medium ${category === 'LOW' ? 'text-green-700' : 'text-gray-500'}`}>
              LOW
            </div>
            <div className={`text-xs ${category === 'LOW' ? 'text-green-600' : 'text-gray-400'}`}>1.0 - 3.3</div>
          </div>
          <div
            className={`rounded py-2 text-center ${
              category === 'MODERATE' ? 'border-2 border-yellow-500 bg-yellow-100' : 'bg-gray-50'
            }`}
          >
            <div className={`text-xs font-medium ${category === 'MODERATE' ? 'text-yellow-700' : 'text-gray-500'}`}>
              MODERATE
            </div>
            <div className={`text-xs ${category === 'MODERATE' ? 'text-yellow-600' : 'text-gray-400'}`}>
              3.4 - 6.6
            </div>
          </div>
          <div
            className={`rounded py-2 text-center ${
              category === 'HIGH' ? 'border-2 border-red-500 bg-red-100' : 'bg-gray-50'
            }`}
          >
            <div className={`text-xs font-medium ${category === 'HIGH' ? 'text-red-700' : 'text-gray-500'}`}>
              HIGH
            </div>
            <div className={`text-xs ${category === 'HIGH' ? 'text-red-600' : 'text-gray-400'}`}>6.7 - 10.0</div>
          </div>
        </div>

        <div className="mb-5 flex justify-center gap-2">
          <button
            type="button"
            onClick={() => setDraft(2.0)}
            className="rounded-full bg-green-100 px-3 py-1 text-xs text-green-700 hover:bg-green-200"
          >
            Low (2.0)
          </button>
          <button
            type="button"
            onClick={() => setDraft(5.0)}
            className="rounded-full bg-yellow-100 px-3 py-1 text-xs text-yellow-700 hover:bg-yellow-200"
          >
            Moderate (5.0)
          </button>
          <button
            type="button"
            onClick={() => setDraft(8.0)}
            className="rounded-full bg-red-100 px-3 py-1 text-xs text-red-700 hover:bg-red-200"
          >
            High (8.0)
          </button>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact cycle picker matching create SOX mapping:
 * selected chips with colors + chevron panel of colored toggles (not giant white cards).
 */
function ReportsEditSoxCyclePicker({ selectedCycles, availableCycles, onChange, onAddCustom }) {
  const [open, setOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const selected = Array.isArray(selectedCycles) ? selectedCycles : [];

  const toggle = (code) => {
    const next = selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code];
    onChange(next);
  };

  const handleAddCustom = () => {
    const err = reportsEditValidateCustomSoxCycle(newCode, newName, REPORTS_EDIT_SOX_CYCLES, availableCycles);
    if (err) {
      toast.error(err);
      return;
    }
    const code = newCode.trim().toUpperCase();
    const name = newName.trim();
    onAddCustom({ code, name, description: name });
    onChange([...selected, code]);
    setNewCode('');
    setNewName('');
    setShowAddForm(false);
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {selected.length === 0 && (
          <span className="text-xs italic text-gray-400">No cycles</span>
        )}
        {selected.map((code) => {
          const meta = availableCycles.find((c) => c.code === code);
          return (
            <span
              key={code}
              title={meta?.name || code}
              className={`rounded border px-2 py-0.5 text-xs font-medium ${reportsEditSoxCycleColor(code)}`}
            >
              {code}
            </span>
          );
        })}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="ml-0.5 rounded p-1 hover:bg-gray-200"
          title="Select SOX cycles"
        >
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[320px] rounded-lg border border-gray-200 bg-white p-3 shadow-xl">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
            Select SOX Cycles
          </h4>
          <div className="mb-3 flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
            {availableCycles.map((cycle) => {
              const on = selected.includes(cycle.code);
              return (
                <button
                  key={cycle.code}
                  type="button"
                  title={cycle.name}
                  onClick={() => toggle(cycle.code)}
                  className={`rounded border px-2 py-1 text-xs font-semibold transition-all ${
                    on
                      ? `${reportsEditSoxCycleColor(cycle.code)} ring-2 ring-offset-1 ring-blue-400`
                      : 'border-gray-200 bg-white text-gray-400 opacity-60 hover:opacity-100'
                  }`}
                >
                  {cycle.code}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              className="rounded border border-dashed border-gray-300 px-2 py-1 text-xs font-semibold text-gray-500 hover:border-blue-400 hover:text-blue-600"
            >
              + Custom
            </button>
          </div>

          {showAddForm && (
            <div className="mb-3 space-y-2 rounded border border-blue-200 bg-blue-50 p-2">
              <input
                type="text"
                placeholder="Code (e.g. ABC)"
                value={newCode}
                maxLength={3}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              />
              <input
                type="text"
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={handleAddCustom}
                  className="flex-1 rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewCode('');
                    setNewName('');
                  }}
                  className="flex-1 rounded bg-gray-200 px-2 py-1 text-xs text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end border-t border-gray-100 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LineItemEditorRow({
  item,
  statement,
  index,
  onChange,
  onDelete,
  onEditRisk,
  availableCycles,
  onAddCustomCycle,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);
  const cats = categoryOptionsForStatement(statement);

  useEffect(() => {
    if (!editing) setDraft(item);
  }, [item, editing]);

  const startEdit = () => {
    setDraft({ ...item });
    setEditing(true);
  };

  const saveEdit = () => {
    const label = String(draft.label || '').trim();
    if (!label) {
      toast.error('Line item name is required');
      return;
    }
    onChange(index, {
      ...draft,
      label,
      line_item: label,
      value: Number(draft.value) || 0,
      category: String(draft.category || cats[0]).toLowerCase(),
      notes: draft.notes != null ? String(draft.notes) : '',
      risk_score: reportsEditClampScore(draft.risk_score),
      risk_level: reportsEditRiskCategory(draft.risk_score),
      sox_cycles: Array.isArray(draft.sox_cycles) ? draft.sox_cycles : [],
      sox_cycle: Array.isArray(draft.sox_cycles) && draft.sox_cycles[0] ? draft.sox_cycles[0] : null,
    });
    setEditing(false);
  };

  const applyCycles = (nextCycles) => {
    setDraft((prev) => ({
      ...prev,
      sox_cycles: nextCycles,
      sox_cycle: nextCycles[0] || null,
    }));
  };

  const openRisk = () => {
    const current = editing ? draft : item;
    onEditRisk(index, current, (score) => {
      const nextScore = reportsEditClampScore(score);
      const nextLevel = reportsEditRiskCategory(nextScore);
      if (editing) {
        setDraft((prev) => ({
          ...prev,
          risk_score: nextScore,
          risk_level: nextLevel,
        }));
      }
    });
  };

  const riskCat = reportsEditRiskCategory(editing ? draft.risk_score : item.risk_score);
  const riskVal = reportsEditClampScore(editing ? draft.risk_score : item.risk_score);

  if (editing) {
    return (
      <tr className="border-b border-gray-100 bg-blue-50/40">
        <td className="px-3 py-2 align-top">
          <input
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={draft.label || ''}
            onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
          />
        </td>
        <td className="px-3 py-2 align-top">
          <select
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={draft.category || cats[0]}
            onChange={(e) => setDraft((p) => ({ ...p, category: e.target.value }))}
          >
            {cats.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 align-top">
          <input
            type="number"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={draft.value ?? 0}
            onChange={(e) => setDraft((p) => ({ ...p, value: e.target.value }))}
          />
        </td>
        <td className="px-3 py-2 align-top">
          <button
            type="button"
            onClick={openRisk}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${riskBadgeClass(riskCat)}`}
          >
            {riskVal.toFixed(1)} — {riskCat}
          </button>
        </td>
        <td className="relative z-20 px-3 py-2 align-top">
          <input
            className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="Add notes..."
            value={draft.notes || ''}
            onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
          />
          <ReportsEditSoxCyclePicker
            selectedCycles={draft.sox_cycles || []}
            availableCycles={availableCycles}
            onChange={applyCycles}
            onAddCustom={onAddCustomCycle}
          />
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex gap-1">
            <button type="button" onClick={saveEdit} className="rounded p-1 text-green-700 hover:bg-green-50" title="Apply">
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(item);
                setEditing(false);
              }}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => onDelete(index)} className="rounded p-1 text-red-600 hover:bg-red-50" title="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-gray-100">
      <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.label}</td>
      <td className="px-3 py-2 text-sm capitalize text-gray-700">{item.category}</td>
      <td className="px-3 py-2 text-sm text-gray-800">{formatMoney(item.value)}</td>
      <td className="px-3 py-2 text-sm">
        <button
          type="button"
          onClick={openRisk}
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${riskBadgeClass(riskCat)}`}
        >
          {riskVal.toFixed(1)} — {riskCat}
        </button>
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">
        <div>{item.notes?.trim() ? item.notes : <span className="italic text-gray-400">No notes</span>}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {(item.sox_cycles || []).map((code) => (
            <span
              key={code}
              className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${reportsEditSoxCycleColor(code)}`}
            >
              {code}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button type="button" onClick={startEdit} className="rounded p-1 text-indigo-600 hover:bg-indigo-50">
            <Pencil className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onDelete(index)} className="rounded p-1 text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Isolated Assessment Reports editor. Does not import Materiality.js helpers.
 */
export default function AssessmentReportsEditPanel({ assessment, onCancel, onSaved }) {
  const [soxDraft, setSoxDraft] = useState(() => normalizeRiskScoresShape(assessment?.sox_cycle_json));
  const [saving, setSaving] = useState(false);
  const [riskModal, setRiskModal] = useState(null);

  const bsItems = soxDraft?.riskScores?.balance_sheet?.items || [];
  const isItems = soxDraft?.riskScores?.income_statement?.items || [];
  const customSoxCycles = Array.isArray(soxDraft?.customSoxCycles) ? soxDraft.customSoxCycles : [];
  const availableCycles = useMemo(
    () => [...REPORTS_EDIT_SOX_CYCLES, ...customSoxCycles],
    [customSoxCycles]
  );

  const updateItem = useCallback((statement, index, nextItem) => {
    setSoxDraft((prev) => {
      const next = deepClone(prev);
      next.riskScores[statement].items[index] = nextItem;
      return next;
    });
  }, []);

  const deleteItem = useCallback((statement, index) => {
    setSoxDraft((prev) => {
      const next = deepClone(prev);
      next.riskScores[statement].items.splice(index, 1);
      return next;
    });
  }, []);

  const addItem = useCallback((statement) => {
    const cats = categoryOptionsForStatement(statement);
    setSoxDraft((prev) => {
      const next = deepClone(prev);
      next.riskScores[statement].items.push({
        label: 'New line item',
        line_item: 'New line item',
        category: cats[0],
        value: 0,
        risk_score: 5,
        risk_level: 'MODERATE',
        notes: '',
        sox_cycles: ['ELC'],
        sox_cycle: 'ELC',
        units: 'USD',
      });
      return next;
    });
  }, []);

  const addCustomCycle = useCallback((cycle) => {
    setSoxDraft((prev) => {
      const next = deepClone(prev);
      if (!Array.isArray(next.customSoxCycles)) next.customSoxCycles = [];
      next.customSoxCycles.push(cycle);
      return next;
    });
  }, []);

  const openRiskModal = (statement, index, item, onApplied) => {
    setRiskModal({
      statement,
      index,
      label: item.label,
      score: item.risk_score,
      onApplied: typeof onApplied === 'function' ? onApplied : null,
    });
  };

  const saveRiskModal = (score) => {
    if (!riskModal) return;
    const { statement, index, onApplied } = riskModal;
    const nextScore = reportsEditClampScore(score);
    const nextLevel = reportsEditRiskCategory(nextScore);
    setSoxDraft((prev) => {
      const next = deepClone(prev);
      const item = next.riskScores[statement].items[index];
      if (item) {
        item.risk_score = nextScore;
        item.risk_level = nextLevel;
      }
      return next;
    });
    onApplied?.(nextScore);
    setRiskModal(null);
  };

  const buildSavePayload = () => {
    const sox = deepClone(soxDraft);
    if (sox.lineItems && typeof sox.lineItems === 'object') {
      sox.lineItems = {
        ...sox.lineItems,
        balance_sheet: {
          ...(sox.lineItems.balance_sheet || {}),
          items: (sox.riskScores.balance_sheet.items || []).map((i) => ({
            ...(typeof i === 'object' ? i : {}),
            label: i.label,
            line_item: i.label,
            category: i.category,
            value: i.value,
            notes: i.notes,
            units: i.units || 'USD',
          })),
        },
        income_statement: {
          ...(sox.lineItems.income_statement || {}),
          items: (sox.riskScores.income_statement.items || []).map((i) => ({
            ...(typeof i === 'object' ? i : {}),
            label: i.label,
            line_item: i.label,
            category: i.category,
            value: i.value,
            notes: i.notes,
            units: i.units || 'USD',
          })),
        },
      };
    }

    // Keep lineItemNotes in sync with item.notes (incl. cleared) so report reload
    // does not restore old notes via mergeLineItemNotesIntoRawData fallback.
    const lineItemNotes = {};
    const bsItems = sox.riskScores?.balance_sheet?.items || [];
    const isItems = sox.riskScores?.income_statement?.items || [];
    bsItems.forEach((item, idx) => {
      lineItemNotes[`bs_0_${idx}`] = item?.notes != null ? String(item.notes) : '';
    });
    isItems.forEach((item, idx) => {
      const note = item?.notes != null ? String(item.notes) : '';
      lineItemNotes[`is_0_${idx}`] = note;
      lineItemNotes[`income_statement_0_${idx}`] = note;
      if (String(item?.category || '').toLowerCase() === 'revenue') {
        lineItemNotes[`is_revenue_0_${idx}`] = note;
      }
      if (String(item?.category || '').toLowerCase() === 'expense') {
        lineItemNotes[`is_expense_0_${idx}`] = note;
      }
    });
    sox.lineItemNotes = lineItemNotes;

    return {
      analysis_name: assessment.analysis_name,
      documents_json: assessment.documents_json || null,
      materiality_json: assessment.materiality_json,
      sox_cycle_json: sox,
      entityscoping_json: assessment.entityscoping_json || null,
      ...(assessment.audit_year_id ? { audit_year_id: assessment.audit_year_id } : {}),
    };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = buildSavePayload();
      const data = await updateMaterialityAssessmentReport(assessment.id, payload);
      if (!data?.success && !data?.id) {
        throw new Error(data?.error || 'Update failed');
      }
      toast.success('Assessment updated successfully');
      onSaved?.({
        ...assessment,
        ...payload,
        id: assessment.id,
        analysis_name: payload.analysis_name,
        documents_json: payload.documents_json,
        materiality_json: payload.materiality_json,
        sox_cycle_json: payload.sox_cycle_json,
        entityscoping_json: payload.entityscoping_json,
      });
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err?.response?.data?.error || err.message || 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  const tables = useMemo(
    () => [
      { key: 'balance_sheet', title: 'Balance Sheet', items: bsItems },
      { key: 'income_statement', title: 'Income Statement', items: isItems },
    ],
    [bsItems, isItems]
  );

  return (
    <div className="rounded-lg border border-indigo-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Edit Assessment</h2>
          <p className="text-sm text-gray-500">
            Update line items, risk scores, and SOX cycles.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {tables.map((table) => (
        <div key={table.key} className="mb-6 overflow-visible rounded-lg border border-gray-200">
          <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
            <h3 className="text-sm font-semibold text-gray-800">{table.title}</h3>
            <button
              type="button"
              onClick={() => addItem(table.key)}
              className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Add line item
            </button>
          </div>
          <div className="overflow-x-auto overflow-y-visible">
            <table className="min-w-full">
              <thead>
                <tr className="border-b bg-white text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">Line Item</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Value</th>
                  <th className="px-3 py-2">Risk Score</th>
                  <th className="px-3 py-2">Notes / SOX Cycles</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {table.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                      No line items. Click Add line item.
                    </td>
                  </tr>
                ) : (
                  table.items.map((item, index) => (
                    <LineItemEditorRow
                      key={`${table.key}-${index}-${item.label}`}
                      item={item}
                      statement={table.key}
                      index={index}
                      onChange={(idx, next) => updateItem(table.key, idx, next)}
                      onDelete={(idx) => deleteItem(table.key, idx)}
                      onEditRisk={(idx, row, onApplied) => openRiskModal(table.key, idx, row, onApplied)}
                      availableCycles={availableCycles}
                      onAddCustomCycle={addCustomCycle}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <RiskScoreModal
        open={Boolean(riskModal)}
        label={riskModal?.label}
        score={riskModal?.score}
        onClose={() => setRiskModal(null)}
        onSave={saveRiskModal}
      />
    </div>
  );
}
